import Anthropic from '@anthropic-ai/sdk';
import { config } from '../../config/config';
import { anthropicProxyFetch } from './RoutyAgent';
import { lookupPropertyRecords } from './propertyRecordsAgent';
import type {
  AppointmentSummaryData,
  AppointmentBriefing,
  AppointmentBriefingMode,
  AppointmentMeetingInsight,
  AppointmentInsightPoint,
  AppointmentQuestionGroup,
} from '../../services/aiSummaryService';

const MAX_TOKENS = 1400;
// Summary-only generations skip the sales-opportunity analysis entirely, so
// they need far fewer output tokens — which is most of what makes them fast.
const SUMMARY_MAX_TOKENS = 600;

const TOOL_NAME = 'submit_appointment_briefing';

const SHARED_INTRO = `You are Routy, an assistant that prepares field/sales teams for upcoming appointments. You will be given structured JSON details about one appointment: event type, customer, address, scheduled time, lead source, how/when it was booked, the customer's own project description, booking-form question/answer pairs (some flagged as needing special attention), the customer-facing note, internal notes left by staff (preBookingNotes on the request before booking and internalNotes on the appointment, each optionally titled), a cancellation reason if applicable, and logistics flags (access code on file, photos attached, expected duration).`;

const SYSTEM_PROMPT = `${SHARED_INTRO}

STEP 1 — Write the plain summary (always):
- headline: one sentence naming what this appointment is and who it's with.
- keyPoints: 3-8 short bullet points (plain English, ~25 words each) covering whatever is actually notable: lead source, how/when it was booked, the project description, flagged or descriptive form answers, the customer note, EVERY internal note provided (don't skip any — each one was deliberately left by staff and may contain critical on-site context like "back patio" or "dog in yard"), access codes, photos, duration, and the cancellation reason if present. Skip anything not provided in the data — never pad with filler or invent details.

STEP 2 — Extract before-arrival instructions (only when applicable):
- beforeArrival: 0-5 short imperative bullets covering ONLY things the assignee must know or do BEFORE arriving or at the door: call/text-ahead requests, pet or safety warnings, gate/access/entry codes and instructions, parking or entry logistics, and specific items/materials to bring or have ready. Pull them from ANY source in the data (notes, internal notes, customer note, form answers, project description). Word each as a direct instruction with its reason when known (e.g. "Call the customer before arrival — dog inside the house.", "Gate code 2134 if no one is home.", "Bring color samples — customer asked for color/palette guidance."). A detail that belongs here goes ONLY here — do NOT repeat it as a keyPoint. Omit the field entirely when nothing applies; NEVER invent instructions.

STEP 3 — Group the customer context (only when the data actually contains it):
- salesNotes: 0-5 short bullets of customer context that shapes the conversation: buying stage ("still researching", "ready to start"), timeline or lack of one, signs they are comparison shopping or gathering quotes, stated decision criteria or preferences, and openings that could differentiate you (e.g. they asked for design/color guidance). Facts only, grounded in the data — this is context, not coaching (tactics belong in salesAngle).
- A detail that belongs in salesNotes goes ONLY there — do NOT repeat it as a keyPoint. Omit the field entirely when nothing applies; NEVER invent context.

STEP 4 — Decide if this is a sales opportunity (be conservative):
Set isSalesOpportunity = true ONLY when the event type and/or the answers make it clearly a sales/estimate/quote-type meeting where someone is trying to win a job (e.g. event type contains "Sales", "Estimate", "Quote", "Consultation", OR the answers describe a project someone is deciding whether to hire out). Set it to false for service/repair/maintenance visits, internal/ad-hoc stops, deliveries, or anything where the intent is unclear. When false, omit meetingTypeLabel/meetingTypeDescription/salesAngle/leadingQuestions entirely — do not guess.

STEP 5 — If (and only if) isSalesOpportunity is true, add sales coaching grounded in THIS appointment's specific details (never generic boilerplate):
- meetingTypeLabel: a short, specific label for the kind of sales situation this is (e.g. "Multi-scope estimate tied to a real estate closing — competitive shopping situation"), based on real signals in the answers/notes (deadlines, competing quotes, multiple scope tiers, urgency, etc.).
- meetingTypeDescription: 1-2 sentences on WHY it's that kind of situation and what that implies about how the customer is deciding.
- salesAngle: 3-5 points, each a short lead-in (title) plus 1-2 sentences of concrete tactic (detail) — how to actually win this specific job given the situation (e.g. differentiate on speed/reliability when there's a deadline, bundle small repairs as a trust-builder, show the value delta on tiered pricing rather than "a la carte", lead with outcome/warranty when the customer is comparison-shopping).
- leadingQuestions: group into these categories (skip a category if nothing useful applies, don't force it): "Clarify the situation", "Uncover decision criteria", "Build the bundle case", "Create urgency/next steps", "Close the value". 1-3 short, natural, on-site questions per category that would surface exactly the context that matters for this appointment.

Call the submit_appointment_briefing tool exactly once with your analysis. Never include markdown formatting inside field values.`;

// Summary-only variant: same plain summary + before-arrival extraction, but no
// sales-opportunity analysis at all — much less for the model to reason about
// and write, so it comes back noticeably faster.
const SUMMARY_ONLY_SYSTEM_PROMPT = `${SHARED_INTRO}

STEP 1 — Write the plain summary (always):
- headline: one sentence naming what this appointment is and who it's with.
- keyPoints: 3-8 short bullet points (plain English, ~25 words each) covering whatever is actually notable: lead source, how/when it was booked, the project description, flagged or descriptive form answers, the customer note, EVERY internal note provided (don't skip any — each one was deliberately left by staff and may contain critical on-site context like "back patio" or "dog in yard"), access codes, photos, duration, and the cancellation reason if present. Skip anything not provided in the data — never pad with filler or invent details.

STEP 2 — Extract before-arrival instructions (only when applicable):
- beforeArrival: 0-5 short imperative bullets covering ONLY things the assignee must know or do BEFORE arriving or at the door: call/text-ahead requests, pet or safety warnings, gate/access/entry codes and instructions, parking or entry logistics, and specific items/materials to bring or have ready. Pull them from ANY source in the data (notes, internal notes, customer note, form answers, project description). Word each as a direct instruction with its reason when known (e.g. "Call the customer before arrival — dog inside the house.", "Gate code 2134 if no one is home.", "Bring color samples — customer asked for color/palette guidance."). A detail that belongs here goes ONLY here — do NOT repeat it as a keyPoint. Omit the field entirely when nothing applies; NEVER invent instructions.

STEP 3 — Group the customer context (only when the data actually contains it):
- salesNotes: 0-5 short bullets of customer context that shapes the conversation: buying stage ("still researching", "ready to start"), timeline or lack of one, signs they are comparison shopping or gathering quotes, stated decision criteria or preferences. Facts only, grounded in the data — no coaching or tactics.
- A detail that belongs in salesNotes goes ONLY there — do NOT repeat it as a keyPoint. Omit the field entirely when nothing applies; NEVER invent context.

Do NOT do any sales analysis. Always set isSalesOpportunity to false and omit meetingTypeLabel/meetingTypeDescription/salesAngle/leadingQuestions entirely.

Call the submit_appointment_briefing tool exactly once with your analysis. Never include markdown formatting inside field values.`;

// Trimmed tool schema for summary-only generations — omitting the sales
// coaching fields keeps the model from being tempted to fill them in.
const SUMMARY_ONLY_TOOL: Anthropic.Tool = {
  name: TOOL_NAME,
  description: 'Submit the structured appointment briefing (plain summary and before-arrival instructions only).',
  input_schema: {
    type: 'object',
    properties: {
      headline: { type: 'string', description: 'One-sentence summary of what this appointment is and who it is with.' },
      keyPoints: {
        type: 'array',
        items: { type: 'string' },
        description: '3-6 short, plain-English bullet points worth knowing before the appointment.',
      },
      beforeArrival: {
        type: 'array',
        items: { type: 'string' },
        description:
          '0-5 imperative instructions to act on BEFORE arriving or at the door (call ahead, pet/safety ' +
          'warnings, gate/access codes, parking, items to bring). Only when the data actually contains ' +
          'them; details listed here must not also appear in keyPoints.',
      },
      salesNotes: {
        type: 'array',
        items: { type: 'string' },
        description:
          '0-5 customer-context bullets shaping the conversation (buying stage, timeline, comparison ' +
          'shopping, decision criteria). Facts grounded in the data, not coaching; details listed here ' +
          'must not also appear in keyPoints.',
      },
      isSalesOpportunity: {
        type: 'boolean',
        description: 'Always false for a summary-only briefing.',
      },
    },
    required: ['headline', 'keyPoints', 'isSalesOpportunity'],
  },
};

const BRIEFING_TOOL: Anthropic.Tool = {
  name: TOOL_NAME,
  description: 'Submit the structured appointment briefing (plain summary, plus sales coaching only when this is genuinely a sales/estimate meeting).',
  input_schema: {
    type: 'object',
    properties: {
      headline: { type: 'string', description: 'One-sentence summary of what this appointment is and who it is with.' },
      keyPoints: {
        type: 'array',
        items: { type: 'string' },
        description: '3-6 short, plain-English bullet points worth knowing before the appointment.',
      },
      beforeArrival: {
        type: 'array',
        items: { type: 'string' },
        description:
          '0-5 imperative instructions to act on BEFORE arriving or at the door (call ahead, pet/safety ' +
          'warnings, gate/access codes, parking, items to bring). Only when the data actually contains ' +
          'them; details listed here must not also appear in keyPoints.',
      },
      salesNotes: {
        type: 'array',
        items: { type: 'string' },
        description:
          '0-5 customer-context bullets shaping the conversation (buying stage, timeline, comparison ' +
          'shopping, decision criteria). Facts grounded in the data, not coaching; details listed here ' +
          'must not also appear in keyPoints.',
      },
      isSalesOpportunity: {
        type: 'boolean',
        description: 'True only when this is clearly a sales/estimate/quote meeting where someone is trying to win a job.',
      },
      meetingTypeLabel: { type: 'string', description: 'Only when isSalesOpportunity is true: a short, specific label for the sales situation.' },
      meetingTypeDescription: { type: 'string', description: 'Only when isSalesOpportunity is true: 1-2 sentences on why it is that kind of situation.' },
      salesAngle: {
        type: 'array',
        description: 'Only when isSalesOpportunity is true: 3-5 specific tactics for winning this job.',
        items: {
          type: 'object',
          properties: {
            title: { type: 'string' },
            detail: { type: 'string' },
          },
          required: ['title', 'detail'],
        },
      },
      leadingQuestions: {
        type: 'array',
        description: 'Only when isSalesOpportunity is true: on-site questions grouped by purpose.',
        items: {
          type: 'object',
          properties: {
            category: { type: 'string' },
            questions: { type: 'array', items: { type: 'string' } },
          },
          required: ['category', 'questions'],
        },
      },
    },
    required: ['headline', 'keyPoints', 'isSalesOpportunity'],
  },
};

interface BriefingToolInput {
  headline: string;
  keyPoints: string[];
  beforeArrival?: string[];
  salesNotes?: string[];
  isSalesOpportunity: boolean;
  meetingTypeLabel?: string;
  meetingTypeDescription?: string;
  salesAngle?: AppointmentInsightPoint[];
  leadingQuestions?: AppointmentQuestionGroup[];
}

/**
 * True when the Claude briefing path can run. Calls go through the backend
 * Anthropic proxy (same as RoutyAgent), which authenticates via the app's
 * bearer token — so the only gate is the Routy feature flag itself.
 */
export function isAppointmentBriefingAgentAvailable(): boolean {
  return config.routy.enabled;
}

/**
 * Generates the briefing with Claude via the backend Anthropic proxy
 * (`/api/anthropic-proxy/v1/messages`, see AnthropicProxyController) — the
 * same routing `RoutyAgent` uses, so no Anthropic key ships to the browser.
 * `AISummaryService.generateAppointmentBriefing` tries the dedicated backend
 * summary endpoint (`docs/implementation/appointment-ai-summary-backend-spec.md`)
 * first and only falls back to this when that call fails, so once that
 * endpoint ships this path stops being used without any other code changes.
 */
export async function generateAppointmentBriefingWithClaude(
  data: AppointmentSummaryData,
  mode: AppointmentBriefingMode = 'salesPrep'
): Promise<AppointmentBriefing> {
  const client = new Anthropic({
    // Dummy key: the proxy strips/ignores it and injects the real key
    // server-side. `dangerouslyAllowBrowser` is safe — no key in the bundle.
    baseURL: `${config.api.baseUrl}/api/anthropic-proxy`,
    apiKey: 'routemize-proxy',
    dangerouslyAllowBrowser: true,
    fetch: anthropicProxyFetch,
  });

  const summaryOnly = mode === 'summary';

  // Public property records (sq ft, beds/baths, year built, last sold) are a
  // separate web-search lookup running in parallel with the briefing — it
  // adds no latency, and a failed lookup never blocks the briefing itself.
  // Deduped/cached per address inside the agent, so regenerating a briefing
  // doesn't re-pay for the searches.
  const propertyPromise = data.address
    ? lookupPropertyRecords(data.address)
    : Promise.resolve(null);

  const response = await client.messages.create({
    model: config.anthropic.model,
    max_tokens: summaryOnly ? SUMMARY_MAX_TOKENS : MAX_TOKENS,
    system: summaryOnly ? SUMMARY_ONLY_SYSTEM_PROMPT : SYSTEM_PROMPT,
    tools: [summaryOnly ? SUMMARY_ONLY_TOOL : BRIEFING_TOOL],
    tool_choice: { type: 'tool', name: TOOL_NAME },
    messages: [
      {
        role: 'user',
        content: `Here is the appointment data as JSON:\n\n${JSON.stringify(data, null, 2)}`,
      },
    ],
  });

  const toolUseBlock = response.content.find(
    (block): block is Anthropic.Messages.ToolUseBlock => block.type === 'tool_use'
  );
  if (!toolUseBlock) {
    throw new Error('Claude did not return a structured appointment briefing.');
  }

  const parsed = toolUseBlock.input as BriefingToolInput;

  const insight: AppointmentMeetingInsight | undefined = !summaryOnly && parsed.isSalesOpportunity
    ? {
        isSalesOpportunity: true,
        meetingTypeLabel: parsed.meetingTypeLabel,
        meetingTypeDescription: parsed.meetingTypeDescription,
        salesAngle: parsed.salesAngle,
        leadingQuestions: parsed.leadingQuestions,
      }
    : undefined;

  const property = await propertyPromise;

  return {
    headline: parsed.headline || 'Appointment summary',
    keyPoints: parsed.keyPoints?.length ? parsed.keyPoints : ['No additional details provided for this appointment.'],
    beforeArrival: parsed.beforeArrival?.length ? parsed.beforeArrival : undefined,
    salesNotes: parsed.salesNotes?.length ? parsed.salesNotes : undefined,
    property: property ?? undefined,
    insight,
    provider: 'Claude',
    cached: false,
  };
}
